import React from 'react';
import { connectCurrentRefinements } from 'react-instantsearch-dom';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { formatMoney } from 'accounting-js';
import { useSelector, useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import getActiveMini from '../../utils/getActiveMini';

import { updateActiveMini } from '../../store/modules/petites/actions';
import Button from '../../atoms/button';
import refinementMap from './utils/refinement-map.json';
import Typography from '../../atoms/typography';
import { logAmplitude } from '../../utils/amplitude';
import { cleanUpFacets, getTotalActiveFilters, refineOrRedirect } from './utils/refinementHelpers';

const LabelWrapper = styled(Button)`
  ${(props) => props.theme.close(props.theme.color.bluePrimary, 'right 1px center', 8)}
  text-decoration: none;
  letter-spacing: normal;
  display: flex;
  flex-direction: row;
  max-height: 3rem;
  position: relative;
  text-overflow: ellipsis;
  width: 100%;

  @media screen and (min-width: ${(props) => props.theme.breakpoint.medium}) {
    ${(props) => props.theme.close(props.theme.color.brand, 'right 1px center', 8)}
  }
`;

const Category = styled(Typography)(({ theme }) => ({
  color: theme.color.brandLightBlue,
  lineHeight: '3rem',
  paddingRight: '0.4rem'
}));

const Facet = styled(Typography)(({ theme }) => ({
  color: theme.color.bluePrimary,
  lineHeight: '3rem'
}));

const RemoveFilter = styled(Button)(({ theme }) => ({
  background: 'transparent',
  borderColor: theme.color.borderBlueLight,
  borderWidth: 1,
  color: theme.color.bluePrimary,
  fontSize: theme.modularScale.fourteen,
  fontFamily: theme.font.sans,
  lineHeight: theme.modularScale.eighteen,
  letterSpacing: 'normal',
  margin: '1rem 0',
  padding: '1rem 0',
  textTransform: 'none',
  outline: 0,
  whiteSpace: 'nowrap',
  width: '100%'
}));

const List = styled.ul(({ hasActiveFilters }) => ({
  paddingBottom: hasActiveFilters ? '1rem' : undefined
}));

const CurrentRefinements = connectCurrentRefinements(({ items, refine }) => {
  const router = useRouter();
  const petites = useSelector((state) => state.petites);
  const dispatch = useDispatch();
  const { totalActiveFilters, price } = getTotalActiveFilters(items);

  const onRefinementClick = (e, item, category) => {
    e.preventDefault();
    const facets = router.query;
    const navigationItem = `${category.id}:${item.label.toLowerCase()}`;

    logAmplitude('Clicked Facet', {
      navigationItem,
      filterAction: 'Filter Removed',
      selected: false,
      facets
    });

    //INFO: when removing girl/boy filter for outlet/kids, the user should be redirected to shop/outlet
    const path = router.asPath;
    if (path.includes('outlet/kids')) {
      refineOrRedirect(item, facets, router, refine);
    } else {
      refine(item.value);
    }
  };

  const handleOnClick = (e, nested, category) => {
    const finalFilterApplied = totalActiveFilters === 1;

    if (finalFilterApplied) {
      const mini = petites.active_mini ? getActiveMini(petites.active_mini, petites.minis) : false;

      if (mini) {
        const filterToRemove = nested.label;
        const isTheLastFilterAppliedToMini = mini.taxons.some(
          (f) => category.id === f.category && filterToRemove === f.value
        );

        if (isTheLastFilterAppliedToMini) {
          dispatch(updateActiveMini(0));
          global.document.cookie = 'maisonette_active_mini=0; path=/';
        }
      }
    }
    onRefinementClick(e, nested, category);
  };

  const handleOnClearAll = () => {
    dispatch(updateActiveMini(0));
    global.document.cookie = 'maisonette_active_mini=0; path=/';

    //INFO: when removing girl/boy filter for outlet/kids, the user should be redirected to shop/outlet
    const path = router.asPath;
    if (path.includes('outlet/kids')) {
      router.push('/shop/outlet');
    } else {
      refine(items);
    }
  };

  return (
    <List hasActiveFilters={items.length > 0}>
      {items.map((item) => {
        if (item.items) {
          return item.items.map((nested) => (
            <li key={`${item.label}-${nested.label}`}>
              <LabelWrapper
                styledLikeLink
                aria-label={`remove filter, ${refinementMap[item.id].label} ${nested.label}`}
                onClick={(e) => handleOnClick(e, nested, refinementMap[item.id])}>
                <Category element="p" like="dec-4">
                  {refinementMap[item.id].label}:
                </Category>
                <Facet element="p" like="dec-4">
                  {nested.label}
                </Facet>
              </LabelWrapper>
            </li>
          ));
        }
      })}

      {price && (
        <LabelWrapper
          styledLikeLink
          aria-label={'remove filter, Price'}
          onClick={(e) => onRefinementClick(e, price, refinementMap[price.id])}>
          <Category element="p" like="dec-4">
            Price:
          </Category>
          <Facet element="p" like="dec-4">
            {`${formatMoney(price.currentRefinement.min, { precision: 0 })} - ${formatMoney(
              price.currentRefinement.max,
              { precision: 0 }
            )}`}
          </Facet>
        </LabelWrapper>
      )}

      {totalActiveFilters > 1 && <RemoveFilter onClick={handleOnClearAll}>Clear All Filters</RemoveFilter>}
    </List>
  );
});

CurrentRefinements.defaultProps = {
  items: []
};

CurrentRefinements.propTypes = {
  items: PropTypes.array,
  refine: PropTypes.func.isRequired
};

const ActiveRefinements = (props) => {
  const removeHiddenFacets = cleanUpFacets(props.isBrandRefined);

  return <CurrentRefinements {...props} transformItems={removeHiddenFacets} />;
};

ActiveRefinements.defaultProps = {
  isBrandRefined: false
};

ActiveRefinements.propTypes = {
  isBrandRefined: PropTypes.bool
};

export default ActiveRefinements;
